import React, { useMemo, useState, useEffect, useCallback } from "react";
import { SessionPartWithContext } from "@/mds/components/sessions/session.types";
import { MDSSessionPart } from "@/mds/components/sessions/session-parts/session-part";
import { extractLatestThinkingText } from "./thought-process.utils";
import { ThoughtProcessHeaderContent } from "./thought-process-header";
import { SESSION_THOUGHT_PROCESS_TEXTS } from "./thought-process.constants";
import {
  StyledReasoningGroup,
  StyledReasoningHeader,
} from "@/mds/components/sessions/session-item/thought-process/thought-process.style";
import { useReasoningGroupUi } from "./use-reasoning-group-ui";

interface ThoughtProcessRendererProps {
  reasoningParts: SessionPartWithContext[];
  isLastInStack: boolean;
  isProcessing: boolean;
  groupId: string;
}

/**
 * Renders a collapsible thought process section that shows reasoning parts.
 * Auto-collapses when processing starts to show current thinking as header text.
 */
export const ThoughtProcessRenderer: React.FC<ThoughtProcessRendererProps> = ({
  reasoningParts,
  isLastInStack,
  isProcessing,
  groupId,
}) => {
  const { isCollapsed, userToggled, toggle, autoCollapse } = useReasoningGroupUi(groupId);
  const [currentThinkingText, setCurrentThinkingText] = useState("");
  const [shouldAnimate, setShouldAnimate] = useState(true);

  // Update thinking text during processing
  useEffect(() => {
    if (!isLastInStack || !isProcessing) return;

    const latestText = extractLatestThinkingText(reasoningParts);
    setCurrentThinkingText(latestText);
  }, [reasoningParts, isLastInStack, isProcessing]);

  // Auto-collapse when processing starts (respects user interaction)
  useEffect(() => {
    if (!isLastInStack || !isProcessing || userToggled || isCollapsed) return;

    autoCollapse();
  }, [isLastInStack, isProcessing, userToggled, autoCollapse, isCollapsed]);

  const headerText = useMemo(() => {
    const isShowingThinkingStatus = isCollapsed && isLastInStack && isProcessing;

    if (isShowingThinkingStatus) {
      return currentThinkingText;
    }

    return isCollapsed
      ? SESSION_THOUGHT_PROCESS_TEXTS.SHOW_COLLAPSED
      : SESSION_THOUGHT_PROCESS_TEXTS.HIDE_EXPANDED;
  }, [isCollapsed, currentThinkingText, isLastInStack, isProcessing]);

  const handleHeaderClick = useCallback(() => {
    toggle();
    setShouldAnimate(false);

    // Re-enable animations when expanding during processing
    const isExpandingDuringProcessing = !isCollapsed && isLastInStack && isProcessing;

    if (isExpandingDuringProcessing) {
      setTimeout(() => setShouldAnimate(true), 0);
    }
  }, [isCollapsed, isLastInStack, isProcessing, toggle]);

  // Hide chevron when showing thinking status
  const isShowingThinkingStatus = isCollapsed && isLastInStack && isProcessing;
  const shouldShowChevron = !isShowingThinkingStatus;

  return (
    <StyledReasoningGroup>
      <StyledReasoningHeader onClick={handleHeaderClick}>
        <ThoughtProcessHeaderContent
          headerText={headerText}
          isCollapsed={isCollapsed}
          shouldShowChevron={shouldShowChevron}
          shouldAnimate={shouldAnimate}
        />
      </StyledReasoningHeader>

      {!isCollapsed && (
        <>
          {reasoningParts.map((partWithContext, index) => {
            return (
              <MDSSessionPart
                key={`${partWithContext.context.eventId}-${index}`}
                part={partWithContext.part}
                context={partWithContext.context}
              />
            );
          })}
        </>
      )}
    </StyledReasoningGroup>
  );
};
